import { PaginatedStoreSettings } from "@/constants/stores";
import { filterNestedObjectArrayBySearchText } from "@/methods/utils";
import { ApiPaginationInfo, PaginatedCardsSettings, PaginatedCardsStore } from "@/types/global";
import { defineStore } from "pinia";

function getInitialPaginatedCardsStore(): PaginatedCardsStore {
  const paginatedCardsStore: PaginatedCardsStore = {
    AllCardsData: {},
    FilteredCardsData: {},
    CurrentSearchText: null,
    CurrentCategoryId: null,
    CurrentPageNumber: 1,
    CardsDownloadData: [],
    ApiPaginationState: {},
    AllowApiCall: {},
    DataLoading: false,
  };
  return JSON.parse(JSON.stringify(paginatedCardsStore));
}

export const createPaginatedCardsStore = (storeId: string) => {
  const storeKey = storeId as keyof typeof PaginatedStoreSettings;
  const storeSettings = PaginatedStoreSettings[storeKey] as PaginatedCardsSettings;
  return defineStore(storeId, {
    state: (): PaginatedCardsStore => getInitialPaginatedCardsStore(),
    getters: {
      StoreSettings(): PaginatedCardsSettings {
        return storeSettings;
      },
      CurrentCategoryData(): Array<any> | null {
        if (this.CurrentCategoryId === null) {
          return null;
        }
        return this.AllCardsData[this.CurrentCategoryId];
      },
      CurrentAllData(): Array<any> | null {
        if (this.CurrentCategoryId === null) {
          return null;
        }
        return this.AllCardsData[this.CurrentCategoryId];
      },
      CurrentFilteredData(): Array<any> | null {
        if (this.CurrentCategoryId === null) {
          return null;
        }
        return this.FilteredCardsData[this.CurrentCategoryId];
      },
      CurrentPageCards(): Array<any> | null {
        if (!this.CurrentFilteredData) {
          return null;
        }
        const startIndex = (this.CurrentPageNumber - 1) * storeSettings.UiPageSize;
        const endIndex = startIndex + storeSettings.UiPageSize;
        return this.CurrentFilteredData.slice(startIndex, endIndex);
      },
      CurrentApiPaginationInfo(): ApiPaginationInfo | null {
        if (this.CurrentCategoryId === null) {
          return null;
        }
        // Normalize a missing slice to null: the category can be selected before its
        // pagination is initialized (e.g. while the first fetch is still in flight), and
        // the downstream `=== null` guards would let `undefined` through.
        return this.ApiPaginationState[this.CurrentCategoryId] ?? null;
      },
      ShowAvailableCount(): boolean {
        if (this.CurrentApiPaginationInfo === null) {
          return false;
        }
        return this.CurrentApiPaginationInfo.totalPages >= 1;
      },
      DisableGetMoreRecords(): boolean {
        if (this.CurrentApiPaginationInfo === null) {
          return false;
        }
        return (
          this.CurrentApiPaginationInfo.totalPages === this.CurrentApiPaginationInfo.pageNumber
        );
      },
    },
    actions: {
      initializeApiPagination() {
        if (this.CurrentCategoryId === null) {
          return;
        }
        this.ApiPaginationState[this.CurrentCategoryId] = {
          pageNumber: 1,
          fetchedCount: 0,
          availableCount: 0,
          totalPages: 0,
        };
      },
      updateCardsList(cardsList: Array<unknown>, selectedCategoryId: number) {
        if (!this.AllCardsData[selectedCategoryId]) {
          this.AllCardsData[selectedCategoryId] = [];
        }
        this.AllCardsData[selectedCategoryId].push(...cardsList);
        this.updateFilteredCards(selectedCategoryId);
        this.resetCurrentPageNumber();
      },
      updateFilteredCards(selectedCategoryId: number) {
        const currentFilteredCardsData = this.AllCardsData[selectedCategoryId];
        if (!currentFilteredCardsData) {
          return;
        }
        // Server-search-only stores (SearchFields === null) skip client-side filtering —
        // the filtered set is just the loaded cards; the domain store re-fetches on search.
        this.FilteredCardsData[selectedCategoryId] = storeSettings.SearchFields
          ? filterNestedObjectArrayBySearchText(
              currentFilteredCardsData,
              storeSettings.SearchFields,
              this.CurrentSearchText,
            )
          : currentFilteredCardsData;
        if (this.FilteredCardsData[selectedCategoryId]) {
          this.CardsDownloadData = Object.values(this.FilteredCardsData[selectedCategoryId]);
        }
      },
      resetCurrentPageNumber() {
        if (!this.CurrentFilteredData) {
          return;
        }
        const totalPages = Math.ceil(this.CurrentFilteredData.length / storeSettings.UiPageSize);
        if (this.CurrentPageNumber > totalPages && totalPages > 0) {
          this.CurrentPageNumber = totalPages;
        }
      },
      setCategoryAndUpdateCards(categoryId: number) {
        this.CurrentCategoryId = categoryId;
        this.CurrentPageNumber = 1;
        this.updateFilteredCards(categoryId);
      },
      onFilterSearchChange(searchedValue: string) {
        if (this.CurrentCategoryId === null) {
          return;
        }
        this.CurrentSearchText = searchedValue;
        this.updateFilteredCards(this.CurrentCategoryId);
        this.CurrentPageNumber = 1;
      },
      onPageChange(currentPage: number) {
        this.CurrentPageNumber = currentPage;
      },
      setStateForGetMoreRecords() {
        if (this.CurrentCategoryId === null) {
          return;
        }
        if (this.CurrentApiPaginationInfo) {
          this.CurrentApiPaginationInfo.pageNumber += 1;
          this.AllowApiCall[this.CurrentCategoryId] = true;
        }
      },
      resetCategoryData(categoryIds: Array<number> | null) {
        if (this.CurrentCategoryId === null) {
          return;
        }
        // This case is used where category radio buttons are not used
        if (categoryIds === null) {
          categoryIds = [0];
        }
        const currentCategoryId = this.CurrentCategoryId;
        categoryIds.forEach((categoryId) => {
          const resetCategoryId = categoryId === 0 ? currentCategoryId : categoryId;
          this.AllCardsData[resetCategoryId] = null;
          this.FilteredCardsData[resetCategoryId] = null;
          this.ApiPaginationState[resetCategoryId] = null;
          this.AllowApiCall[resetCategoryId] = true;
        });
      },
      resetData() {
        this.AllCardsData = {};
        this.FilteredCardsData = {};
        // Stale pagination slices kept accumulating fetchedCount across forced
        // reloads, inflating the "Loaded X / Y records" counter (B42).
        this.ApiPaginationState = {};
        this.AllowApiCall = {};
        this.CurrentPageNumber = 1;
        this.CurrentSearchText = null;
      },
    },
  });
};
